/**
 * HTTP Interceptor con manejo de autenticación y refresh automático de tokens
 * 
 * Flujo:
 * 1. Request con accessToken
 * 2. Backend responde 401 (token expirado)
 * 3. Interceptor detecta 401
 * 4. Llama a /api/auth/refresh con refreshToken
 * 5. Si refresh OK: guardar nuevo accessToken y reintentar request original
 * 6. Si refresh falla: logout definitivo
 */

// ============================================================================
// CONSTANTS & CONFIG
// ============================================================================

const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// ============================================================================
// FLAGS DE CONTROL DE CONCURRENCIA
// ============================================================================

// Flag para evitar múltiples refresh simultáneos
let isRefreshing = false;

// Cola de requests pendientes que esperan el refresh
let pendingRequests = [];

/**
 * Agregar request a la cola de espera
 * @param {Function} retryCallback - Función para reintentar el request
 */
const addPendingRequest = (retryCallback) => {
  pendingRequests.push(retryCallback);
};

/**
 * Reintentar todos los requests pendientes después de un refresh exitoso
 */
const retryPendingRequests = () => {
  pendingRequests.forEach((retryCallback) => {
    try {
      retryCallback();
    } catch (e) {
      console.error('[httpInterceptor] Error retrying request:', e);
    }
  });
  pendingRequests = [];
};

/**
 * Función centralizada de limpieza de sesión
 * Limpia todos los datos de sesión del usuario
 */
const clearSession = () => {
  // Limpiar token y datos de autenticación
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  
  localStorage.removeItem('userData');
  localStorage.removeItem('displayName');
  
  // Limpiar tenant
  localStorage.removeItem('selectedTenant');
  localStorage.removeItem('selectedTenantId');
  localStorage.removeItem('selectedTenantCode');
  localStorage.removeItem('tenantId');
  
  // Limpiar dataArea
  localStorage.removeItem('dataAreaId');
  
  // Dispatch evento para que componentes React actualicen su estado
  window.dispatchEvent(new Event('sessionCleared'));
};

/**
 * Función de logout automático
 * Llamada cuando el refresh token expira o es inválido
 */
const handleAuthError = (isPublicAuthEndpoint) => {
  // Limpiar sesión
  clearSession();
  
  // Redireccionar a login
  setTimeout(() => {
    // Verificar si ya no hay token (para evitar redirect si ya se hizo logout)
    if (!localStorage.getItem(AUTH_TOKEN_KEY) && !sessionStorage.getItem(AUTH_TOKEN_KEY)) {
      window.location.href = '/';
    }
  }, 0);
};

// ============================================================================
// STORAGE HELPER - Support for "Remember Me" (localStorage vs sessionStorage)
// ============================================================================

/**
 * Obtener el storage correcto según el tipo de sesión
 * Si existe token en sessionStorage, usar sessionStorage
 * Si existe token en localStorage, usar localStorage
 * Por defecto usa sessionStorage
 */
const getStorage = () => {
  const tokenInLocal = localStorage.getItem(AUTH_TOKEN_KEY);
  const tokenInSession = sessionStorage.getItem(AUTH_TOKEN_KEY);
  
  if (tokenInLocal) {
    return localStorage;
  } else if (tokenInSession) {
    return sessionStorage;
  }
  // Default: usar localStorage para mantener compatibilidad
  return localStorage;
};

/**
 * Obtener token del storage correcto
 */
const getAuthToken = () => {
  const storage = getStorage();
  return storage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Obtener refresh token del storage correcto
 */
const getRefreshToken = () => {
  const storage = getStorage();
  return storage.getItem(REFRESH_TOKEN_KEY);
};

const extractTenantIdFromJwt = (token) => {
  if (!token) return null;
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return null;
    const payload = JSON.parse(atob(payloadPart.replace(/-/g, "+").replace(/_/g, "/")));
    return payload.tenantId || null;
  } catch {
    return null;
  }
};

const cleanTenantHeader = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  if (!text || text === "null" || text === "undefined" || text.startsWith("{")) {
    return null;
  }
  return text;
};

const resolveRequestPath = (url) => {
  if (typeof url !== "string") {
    return "";
  }
  if (!url.startsWith("http")) {
    return url.split("?")[0];
  }
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const allowsTenantHeader = (url) => {
  const path = resolveRequestPath(url);
  return (
    path.startsWith("/api/tenants/") ||
    path.startsWith("/api/legacy/") ||
    path.startsWith("/api/core/") ||
    path.startsWith("/api/ai-development") ||
    path.startsWith("/api/audit/") ||
    path.startsWith("/api/reverse-sync/") ||
    path.startsWith("/api/factuflow/") ||
    path.startsWith("/api/flow-bpm/") ||
    path.startsWith("/api/hr/") ||
    path.startsWith("/api/ledger/") ||
    path.startsWith("/api/realestate/") ||
    path.startsWith("/api/me")
  );
};

/**
 * Guardar tokens en el storage correcto
 */
const setTokens = (accessToken, refreshToken) => {
  const storage = getStorage();
  storage.setItem(AUTH_TOKEN_KEY, accessToken);
  if (refreshToken) {
    storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

// ============================================================================
// REFRESH TOKEN LOGIC
// ============================================================================

/**
 * Intentar refresh del access token
 * @returns {Promise<{accessToken: string, refreshToken: string}|null>} - Tokens nuevos o null si falla
 */
const attemptTokenRefresh = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    console.warn('[httpInterceptor] No refresh token available');
    return null;
  }
  
  try {
    const apiUrl = process.env.REACT_APP_CORE_BACKEND_URL || process.env.REACT_APP_LOAD_PAGE_URL;
    const fullUrl = `${apiUrl}/api/auth/refresh`;
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });
    
    if (!response.ok) {
      console.warn('[httpInterceptor] Refresh failed with status:', response.status);
      return null;
    }
    
    const data = await response.json();
    const { accessToken, refreshToken: newRefreshToken } = data;
    
    if (!accessToken) {
      console.warn('[httpInterceptor] Refresh response missing accessToken');
      return null;
    }
    
    // Actualizar tokens en storage
    setTokens(accessToken, newRefreshToken || refreshToken);
    
    console.log('[httpInterceptor] Token refreshed successfully');
    return { accessToken, refreshToken: newRefreshToken || refreshToken };
  } catch (error) {
    console.error('[httpInterceptor] Error refreshing token:', error);
    return null;
  }
};

// ============================================================================
// FETCH WITH AUTH
// ============================================================================

/**
 * Fetch mejorado con manejo automático de autenticación y refresh de tokens
 */
export const fetchWithAuth = async (url, options = {}) => {
  const isFactuFlowEndpoint =
    typeof url === "string" &&
    (url.startsWith("/api/factuflow/") || url === "/api/factuflow");
  const isFlowBpmEndpoint =
    typeof url === "string" &&
    (url.startsWith("/api/flow-bpm/") || url === "/api/flow-bpm");
  const isHrEndpoint =
    typeof url === "string" &&
    (url.startsWith("/api/hr/") || url === "/api/hr");
  const isRealEstateEndpoint =
    typeof url === "string" &&
    (url.startsWith("/api/realestate/") || url === "/api/realestate");
  const isLedgerEndpoint =
    typeof url === "string" &&
    (url.startsWith("/api/ledger/") || url === "/api/ledger");

  // Default API base URL (core)
  const apiUrl =
    options.baseUrl ||
    (isFactuFlowEndpoint || isFlowBpmEndpoint || isHrEndpoint || isRealEstateEndpoint || isLedgerEndpoint
      ? ""
      : process.env.REACT_APP_CORE_BACKEND_URL ||
        process.env.REACT_APP_LOAD_PAGE_URL);
  const fullUrl = url.startsWith("http") ? url : `${apiUrl}${url}`;

  // Obtener token del storage correcto
  const token = getAuthToken();

  // Tenant (multi-tenant + RLS)
  const selectedTenant =
    localStorage.getItem("selectedTenant") ||
    localStorage.getItem("selectedTenantId") ||
    localStorage.getItem("selectedTenantCode") ||
    localStorage.getItem("tenantId") ||
    extractTenantIdFromJwt(token);
  const tenantHeader = cleanTenantHeader(selectedTenant);

  // Agregar headers de autenticación
  const headers = {
    ...options.headers,
  };

  // Solo agregar Authorization si hay token y no es un endpoint público
  const isPublicAuthEndpoint =
    url === "/login" ||
    url.startsWith("/api/auth/") ||
    url.startsWith("/actuator/") ||
    url.startsWith("/swagger-ui") ||
    url.startsWith("/v3/api-docs");

  if (token && !isPublicAuthEndpoint) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // X-Tenant header
  if (tenantHeader && !isPublicAuthEndpoint && allowsTenantHeader(url)) {
    headers["X-Tenant"] = tenantHeader;
  }

  // X-DataAreaId header
  const isBootstrapEndpoint = 
    url.includes('/api/legacy/data-areas') || 
    url === '/api/me' ||
    url.startsWith('/api/tenants') ||
    url.startsWith('/api/ai-development');
  const dataAreaId = localStorage.getItem('dataAreaId');
  if (dataAreaId && !isPublicAuthEndpoint && !isBootstrapEndpoint && !isFactuFlowEndpoint && !isFlowBpmEndpoint && !isHrEndpoint && !isRealEstateEndpoint) {
    headers["X-DataAreaId"] = dataAreaId;
  }

  // Agregar Content-Type si no está presente y hay body
  if (options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }

  const config = {
    ...options,
    headers,
  };

  // ============================================================================
  // TRY REQUEST
  // ============================================================================
  
  try {
    let response = await fetch(fullUrl, config);

    // ============================================================================
    // 401 - TOKEN EXPIRADO: INTENTAR REFRESH
    // ============================================================================
    if (response.status === 401 && !isPublicAuthEndpoint) {
      // Si ya hay un refresh en progreso, agregar este request a la cola
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addPendingRequest(async () => {
            try {
              const newResponse = await fetch(fullUrl, config);
              const data = await newResponse.json();
              resolve({ json: () => Promise.resolve(data), ok: newResponse.ok, status: newResponse.status });
            } catch (error) {
              reject(error);
            }
          });
        });
      }
      
      // ============================================================================
      // INICIAR REFRESH
      // ============================================================================
      isRefreshing = true;
      
      try {
        const newTokens = await attemptTokenRefresh();
        
        if (newTokens) {
          // ========================================================================
          // REFRESH EXITOSO - REINTENTAR REQUEST ORIGINAL
          // ========================================================================
          
          // Actualizar headers con nuevo token
          const newHeaders = {
            ...config.headers,
            'Authorization': `Bearer ${newTokens.accessToken}`,
          };
          
          const retryConfig = {
            ...config,
            headers: newHeaders,
          };
          
          // Reintentar request original
          const retryResponse = await fetch(fullUrl, retryConfig);
          
          // Reintentar requests pendientes
          retryPendingRequests();
          isRefreshing = false;
          
          return retryResponse;
        } else {
          // ========================================================================
          // REFRESH FALLÓ - LOGOUT
          // ========================================================================
          retryPendingRequests();
          isRefreshing = false;
          
          const authError = new Error("SESSION_EXPIRED");
          authError.isAuthError = true;
          authError.status = 401;
          
          handleAuthError(isPublicAuthEndpoint);
          throw authError;
        }
      } catch (refreshError) {
        // Error durante el refresh
        isRefreshing = false;
        retryPendingRequests();
        
        if (refreshError.isAuthError) {
          throw refreshError;
        }
        
        const authError = new Error("SESSION_EXPIRED");
        authError.isAuthError = true;
        authError.status = 401;
        
        handleAuthError(isPublicAuthEndpoint);
        throw authError;
      }
    }

    // ============================================================================
    // 403 - FORBIDDEN: ANALIZAR SI ES TOKEN INVÁLIDO
    // ============================================================================
    if (response.status === 403 && !isPublicAuthEndpoint) {
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.clone().json();
          const isAuthError = 
            errorData?.error?.includes('expired') ||
            errorData?.error?.includes('invalid') ||
            errorData?.error?.includes('disabled') ||
            errorData?.error?.includes('suspended') ||
            errorData?.message?.includes('expired') ||
            errorData?.message?.includes('invalid') ||
            errorData?.message?.includes('disabled') ||
            errorData?.message?.includes('suspended') ||
            errorData?.error === 'token_expired' ||
            errorData?.error === 'invalid_token' ||
            errorData?.error === 'invalid_grant';
          
          if (isAuthError) {
            const authError = new Error("SESSION_EXPIRED");
            authError.isAuthError = true;
            authError.status = 403;

            handleAuthError(isPublicAuthEndpoint);
            throw authError;
          }
        }
      } catch (e) {
        // Si no se puede analizar el cuerpo, continuar con el flujo normal de error
      }
    }

    // Si la respuesta no es ok, lanzar error con información
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.clone().json();
          errorMessage =
            data.message ||
            data.error ||
            errorMessage;
        } else {
          errorMessage = await response.clone().text();
        }
      } catch (e) {
        // Si no se puede parsear el cuerpo, usar el status
      }
      const error = new Error(errorMessage);
      error.response = response;
      error.status = response.status;
      throw error;
    }

    return response;
  } catch (error) {
    // Si es un error de red o fetch falló
    if (!error.response && !error.status) {
      error.isNetworkError = true;
    }
    throw error;
  }
};

// ============================================================================
// EXPORT HELPERS
// ============================================================================

/**
 * Helper para peticiones GET
 */
export const get = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: "GET",
  });
  return response.json();
};

/**
 * Helper para peticiones POST
 */
export const post = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: "POST",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  return response.json();
};

/**
 * Helper para peticiones PUT
 */
export const put = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: "PUT",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

/**
 * Helper para peticiones DELETE
 */
export const del = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: "DELETE",
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

// ============================================================================
// BACKEND HELPERS
// ============================================================================

const coreBaseUrl =
  process.env.REACT_APP_CORE_BACKEND_URL || process.env.REACT_APP_LOAD_PAGE_URL;

const legacyBaseUrl =
  process.env.REACT_APP_LEGACY_BACKEND_URL ||
  process.env.REACT_APP_BUSINESS_BACKEND_URL ||
  coreBaseUrl;

const businessBaseUrl = legacyBaseUrl;

export const getCore = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: coreBaseUrl,
    method: "GET",
  });
  return response.json();
};

export const getCoreText = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: coreBaseUrl,
    method: "GET",
  });
  return response.text();
};

export const postCore = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: coreBaseUrl,
    method: "POST",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const putCore = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: coreBaseUrl,
    method: "PUT",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const patchCore = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: coreBaseUrl,
    method: "PATCH",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const delCore = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: coreBaseUrl,
    method: "DELETE",
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const getLegacy = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: legacyBaseUrl,
    method: "GET",
  });
  return response.json();
};

export const postLegacy = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: legacyBaseUrl,
    method: "POST",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  return response.json();
};

export const putLegacy = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: legacyBaseUrl,
    method: "PUT",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const delLegacy = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: legacyBaseUrl,
    method: "DELETE",
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const getBusiness = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: businessBaseUrl,
    method: "GET",
  });
  return response.json();
};

export const postBusiness = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: businessBaseUrl,
    method: "POST",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  return response.json();
};

export const putBusiness = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: businessBaseUrl,
    method: "PUT",
    body: body instanceof FormData ? body : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export const delBusiness = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    baseUrl: businessBaseUrl,
    method: "DELETE",
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

export default fetchWithAuth;
